import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { ChapterRail, ChapterNav } from "@/components/WorkNav";
import { Reveal } from "../components/Reveal";

const RB = "/work/rb";

const SPAN = [
  { name: "Winning 5", sport: "Football", src: `${RB}/winning5.webp` },
  { name: "Neymar Jr's Five", sport: "Football", src: `${RB}/neymar.jpeg` },
  { name: "Four2Score", sport: "Football", src: `${RB}/four2score.png` },
  { name: "Shuttle Up", sport: "Badminton", src: `${RB}/shuttle-logo.jpeg` },
  { name: "Tlang Ruam", sport: "Urban Downhill", src: `${RB}/tlang-logo.png`, dark: true },
];

const JOURNEY = [
  { big: "City", small: "Qualifiers" },
  { big: "City", small: "Winners" },
  { big: "National", small: "Final" },
  { big: "World", small: "Stage" },
];

const CHRONO = [
  { name: "Winning 5s", years: "2016–2017", src: `${RB}/winning5.webp` },
  { name: "Neymar Jr's Five", years: "2020–2022", src: `${RB}/neymar.jpeg` },
  { name: "Four2Score", years: "2023–2026", src: `${RB}/four2score.png` },
];

const CITY_STUDY = [
  ["Living city", "Racecourse"],
  ["Public roads", "Controlled track"],
  ["Existing terrain", "Retrofitted safety"],
  ["Normal city", "Event operation"],
];

const SYNTHESIS = [
  { sport: "Football", word: "Continuity" },
  { sport: "Shuttle Up", word: "Coordination" },
  { sport: "Tlang Ruam", word: "Adaptability" },
];

const Header = () => (
  <header className="fixed top-0 inset-x-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/10">
    <div className="mx-auto max-w-[1500px] w-full px-5 sm:px-8 h-16 flex items-center justify-between">
      <Link to="/" data-testid="site-logo" className="shrink-0">
        <img src="/owos-logo-badge.png" alt="One World of Sports" className="h-11 w-auto object-contain" />
      </Link>
      <div className="flex items-center gap-5 sm:gap-7">
        <Link
          to="/work"
          data-testid="redbull-back-work-top"
          className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-neutral-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          Work
        </Link>
        <Link
          to="/#gateways"
          data-testid="redbull-back-explore-top"
          className="hidden sm:inline text-[11px] uppercase tracking-[0.25em] font-bold text-neutral-500 hover:text-white transition-colors"
        >
          Explore
        </Link>
      </div>
    </div>
  </header>
);

const Kicker = ({ children }) => (
  <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-[#e62429]">
    <span className="h-px w-10 bg-[#e62429]" />
    {children}
  </p>
);

const LogoChip = ({ src, alt, dark }) => (
  <div
    className={`aspect-square w-full flex items-center justify-center p-5 border ${
      dark ? "bg-[#0f0f10] border-white/12" : "bg-neutral-50 border-white/10"
    }`}
  >
    <img src={src} alt={alt} loading="lazy" className="max-h-full max-w-full object-contain" />
  </div>
);

export default function RedBull() {
  const { scrollYProgress } = useScroll();
  useEffect(() => {
    document.title = "Red Bull — Work — One World of Sports";
  }, []);

  return (
    <div data-testid="redbull-page" className="bg-[#0a0a0a] min-h-screen text-white">
      <motion.div
        style={{ scaleX: scrollYProgress }}
        className="fixed top-0 left-0 right-0 h-[3px] bg-[#e62429] origin-left z-[60]"
      />
      <Header />
      <ChapterRail currentKey="redbull" />

      {/* OPENER — One relationship. Many briefs. */}
      <section data-testid="redbull-opener" className="mx-auto max-w-[1500px] px-5 sm:px-8 pt-36 sm:pt-44 pb-16 sm:pb-24">
        <Reveal>
          <div className="flex items-center gap-4">
            <Kicker>Work · Client</Kicker>
            <img src={`${RB}/rb-logo.webp`} alt="Red Bull" className="h-8 w-auto object-contain opacity-90" />
          </div>
          <h1 className="mt-6 font-display uppercase text-white text-[16vw] sm:text-[11vw] lg:text-[9.5rem] leading-[0.82]">
            Red Bull
          </h1>
          <p className="mt-4 font-display uppercase text-[#e62429] text-3xl sm:text-5xl leading-none">
            One relationship. Many briefs.
          </p>
        </Reveal>

        <Reveal className="mt-14">
          <div className="flex items-center justify-between text-2xl sm:text-4xl font-display text-neutral-500">
            <span>2016</span>
            <span className="mx-4 h-px flex-1 bg-white/15" />
            <span>2026</span>
          </div>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {SPAN.map((p) => (
              <div key={p.name} data-testid={`span-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <LogoChip src={p.src} alt={p.name} dark={p.dark} />
                <p className="mt-3 font-display uppercase text-lg leading-none">{p.name}</p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500">{p.sport}</p>
              </div>
            ))}
          </div>
          <p className="mt-12 font-display uppercase text-white text-xl sm:text-3xl leading-tight flex flex-wrap items-center gap-x-3 gap-y-1">
            Support assignments <ArrowRight className="h-6 w-6 text-[#e62429]" /> Lead mandates{" "}
            <ArrowRight className="h-6 w-6 text-[#e62429]" /> Multi-property delivery
          </p>
        </Reveal>
      </section>

      {/* FOOTBALL — three identities */}
      <section data-testid="redbull-football" className="border-t border-white/10 bg-[#0f0f0f] py-20 sm:py-28">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <Kicker>Red Bull</Kicker>
            <h2 className="mt-5 font-display uppercase text-white text-5xl sm:text-7xl leading-none">Football</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {CHRONO.map((c) => (
              <Reveal key={c.name}>
                <LogoChip src={c.src} alt={c.name} />
                <p className="mt-4 font-display uppercase text-2xl leading-none">{c.name}</p>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-12 font-display uppercase text-[#e62429] text-2xl sm:text-4xl leading-tight max-w-4xl">
              Three identities. One continuing operating relationship.
            </p>
          </Reveal>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="mx-auto max-w-[1500px] px-5 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.9] max-w-4xl">
            A global property. An Indian competition journey.
          </h2>
        </Reveal>
        <Reveal className="mt-14">
          <div className="flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-3">
            {JOURNEY.map((j, i) => (
              <div key={j.small} className="flex items-end gap-3 sm:gap-4">
                <div>
                  <p className="font-display uppercase text-3xl sm:text-4xl text-[#e62429] leading-none">{j.big}</p>
                  <p className="mt-1 font-display uppercase text-xl sm:text-2xl text-white leading-none">{j.small}</p>
                </div>
                {i < JOURNEY.length - 1 && <ArrowRight className="h-6 w-6 text-neutral-600 mb-1 rotate-90 sm:rotate-0" />}
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal>
          <p className="mt-12 max-w-3xl text-lg sm:text-xl leading-relaxed text-neutral-300">
            Compact formats move players from local competition towards a National Final — and, where the global
            property provides it, a World Final opportunity.
          </p>
          <p className="mt-8 font-display uppercase text-[#e62429] text-xl sm:text-3xl leading-tight">
            Oneworld of Sports: ground-up delivery of the Indian competition.
          </p>
        </Reveal>
      </section>

      {/* CHRONOLOGY — successive properties */}
      <section className="border-y border-white/10 bg-[#0f0f0f] py-20 sm:py-28">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.9]">
              Successive properties. Continuous delivery.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-8">
            {CHRONO.map((c, i) => (
              <Reveal key={c.name} className="relative">
                <LogoChip src={c.src} alt={c.name} />
                <p className="mt-4 font-display uppercase text-xl leading-none">{c.name}</p>
                <p className="mt-2 font-display text-2xl text-[#e62429]">{c.years}</p>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-12 font-display text-4xl sm:text-5xl text-neutral-400">
              2016 <span className="text-[#e62429]">→</span> 2026
            </p>
          </Reveal>
        </div>
      </section>

      {/* FOOTPRINT MAP */}
      <section className="mx-auto max-w-[1500px] px-5 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.9]">
            The football platform travelled.
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-8">
            <div className="bg-neutral-50 p-4 sm:p-6 border border-white/10">
              <img
                src={`${RB}/football-map.png`}
                alt="23-city football footprint across India"
                data-testid="football-map"
                className="w-full h-auto object-contain"
              />
            </div>
          </Reveal>
          <Reveal className="lg:col-span-4">
            <p className="font-display text-7xl sm:text-8xl text-[#e62429] leading-none">23</p>
            <p className="font-display uppercase text-3xl text-white leading-none mt-1">Cities</p>
            <p className="mt-4 font-display text-2xl text-neutral-400">2020–2026</p>
            <p className="mt-4 text-sm uppercase tracking-[0.2em] font-bold text-neutral-500">
              Neymar Jr's Five → Four2Score
            </p>
            <p className="mt-6 text-xs uppercase tracking-[0.25em] font-bold text-neutral-600">
              23-city footprint · India
            </p>
          </Reveal>
        </div>
      </section>

      {/* SHUTTLE UP */}
      <section data-testid="redbull-shuttleup" className="relative">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="order-2 lg:order-1 bg-[#0f0f0f] px-5 sm:px-8 lg:px-16 py-16 sm:py-24 flex flex-col justify-center">
            <Reveal>
              <Kicker>Red Bull</Kicker>
              <h2 className="mt-5 font-display uppercase text-white text-5xl sm:text-7xl leading-none">Shuttle Up</h2>
              <p className="mt-8 font-display uppercase text-[#e62429] text-2xl sm:text-3xl leading-tight">
                Girls-only. Multi-city. National Final.
              </p>
            </Reveal>
          </div>
          <div className="order-1 lg:order-2 min-h-[52vh] lg:min-h-[70vh]">
            <img src={`${RB}/shuttle-action.webp`} alt="Red Bull Shuttle Up National Finals 2022" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* SHUTTLE — same principle */}
      <section className="mx-auto max-w-[1500px] px-5 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.9]">
            Same travelling principle. Different competition.
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-7">
            <img src={`${RB}/shuttle-court.webp`} alt="Shuttle Up court, National Finals 2022" className="w-full h-auto border border-white/10" />
          </Reveal>
          <Reveal className="lg:col-span-5">
            <p className="flex items-baseline gap-4">
              <span className="font-display text-6xl text-[#e62429] leading-none">5</span>
              <span className="font-display uppercase text-3xl text-white">Cities</span>
            </p>
            <p className="mt-6 flex items-center gap-3 font-display uppercase text-3xl text-white">
              <ArrowRight className="h-6 w-6 text-[#e62429]" /> Chennai
            </p>
            <p className="mt-1 text-xs uppercase tracking-[0.25em] font-bold text-neutral-500">National Final</p>
            <p className="mt-6 text-lg leading-relaxed text-neutral-300 max-w-sm">
              A girls-only badminton property delivered across five cities in 2022.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SHUTTLE — operate / compete / conclude */}
      <section className="border-t border-white/10 bg-[#0f0f0f] py-20 sm:py-28">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.9]">
              From city courts to National Final.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { src: `${RB}/shuttle-court.webp`, label: "Operate" },
              { src: `${RB}/shuttle-winners.webp`, label: "Compete" },
              { src: `${RB}/shuttle-participants.webp`, label: "Conclude" },
            ].map((p) => (
              <Reveal key={p.label}>
                <img src={p.src} alt={p.label} className="w-full aspect-[4/3] object-cover border border-white/10" />
                <p className="mt-3 text-xs uppercase tracking-[0.3em] font-bold text-[#e62429]">{p.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TLANG RUAM — opener */}
      <section data-testid="redbull-tlangruam" className="relative min-h-[80vh] flex items-end overflow-hidden isolate">
        <img src={`${RB}/tlang-drone.jpeg`} alt="Aizawl street course, Red Bull Tlang Ruam" className="absolute inset-0 z-0 h-full w-full object-cover" />
        <div className="absolute inset-0 z-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/40 to-[#0a0a0a]/20" />
        <div className="relative z-10 mx-auto max-w-[1500px] w-full px-5 sm:px-8 pb-14 sm:pb-20">
          <img src={`${RB}/tlang-logo.png`} alt="Red Bull Aizawl Tlang Ruam" className="h-20 sm:h-28 w-auto object-contain mb-6" />
          <h2 className="font-display uppercase text-white text-5xl sm:text-7xl leading-[0.9]">
            Aizawl becomes<br />the racecourse.
          </h2>
          <p className="mt-4 font-display text-2xl text-[#e62429]">2024 + 2025</p>
        </div>
      </section>

      {/* TLANG — living city */}
      <section className="mx-auto max-w-[1500px] px-5 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.9]">
            An urban downhill race through a living city.
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-7">
            <img src={`${RB}/tlang-action.webp`} alt="Urban downhill rider, Aizawl" className="w-full h-auto border border-white/10" />
          </Reveal>
          <Reveal className="lg:col-span-5">
            <p className="font-display uppercase text-[#e62429] text-3xl leading-none">Tlang Ruam</p>
            <p className="mt-4 font-display uppercase text-2xl text-white leading-tight">
              Red Bull's urban downhill cycling race in Aizawl.
            </p>
            <p className="mt-6 text-lg leading-relaxed text-neutral-300">Not a closed sporting venue.</p>
            <p className="mt-4 text-lg leading-relaxed text-neutral-300">
              A high-speed course built through streets, stairs, ramps and the everyday fabric of the city.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TLANG — credentials */}
      <section className="relative">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="bg-neutral-50 px-5 sm:px-8 lg:px-16 py-16 sm:py-24 flex flex-col justify-center">
            <Reveal>
              <h2 className="font-display uppercase text-[#0a0a0a] text-4xl sm:text-6xl leading-[0.9]">
                Our cycling credentials?
              </h2>
              <p className="mt-4 font-display uppercase text-[#e62429] text-6xl sm:text-8xl leading-none">None.</p>
              <p className="mt-10 font-display uppercase text-[#0a0a0a] text-2xl sm:text-3xl">And that may have helped.</p>
              <p className="mt-4 text-lg leading-relaxed text-neutral-700 max-w-md">
                We had conventions to learn — but none to unlearn.
              </p>
            </Reveal>
          </div>
          <div className="min-h-[52vh] lg:min-h-0">
            <img src={`${RB}/tlang-trackwalk.webp`} alt="Oneworld at the Aizawl track walk" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* TLANG — course maps */}
      <section className="mx-auto max-w-[1500px] px-5 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.9]">
            The race around the course.
          </h2>
          <p className="mt-2 font-display uppercase text-[#e62429] text-3xl sm:text-5xl leading-none">
            Not the course around the race.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-8">
          {[
            { src: `${RB}/tlang-map2024.webp`, year: "2024" },
            { src: `${RB}/tlang-map2025.webp`, year: "2025" },
          ].map((m) => (
            <Reveal key={m.year}>
              <div className="bg-neutral-50 p-4 border border-white/10">
                <img src={m.src} alt={`Tlang Ruam course map ${m.year}`} className="w-full h-auto object-contain" />
              </div>
              <p className="mt-3 font-display text-2xl text-neutral-400 text-center">{m.year}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TLANG — study the city */}
      <section className="border-y border-white/10 bg-[#0f0f0f] py-20 sm:py-28">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.9]">
              Study the city. Build the race.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 space-y-5">
              {CITY_STUDY.map(([a, b]) => (
                <Reveal key={a}>
                  <div className="flex items-center gap-4 border-b border-white/10 pb-5">
                    <span className="font-display uppercase text-lg sm:text-2xl text-neutral-400 flex-1">{a}</span>
                    <ArrowRight className="h-5 w-5 text-[#e62429] shrink-0" />
                    <span className="font-display uppercase text-lg sm:text-2xl text-white flex-1 text-right">{b}</span>
                  </div>
                </Reveal>
              ))}
              <Reveal>
                <p className="pt-4 font-display uppercase text-[#e62429] text-2xl sm:text-3xl flex flex-wrap items-center gap-x-3">
                  Learn <ArrowRight className="h-5 w-5" /> Adapt <ArrowRight className="h-5 w-5" /> Deliver
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Reveal>
                <img src={`${RB}/tlang-director.webp`} alt="Oneworld crew, Aizawl" className="w-full h-auto object-contain border border-white/10" />
              </Reveal>
              <Reveal>
                <img src={`${RB}/tlang-ramp.webp`} alt="Start ramp, Tlang Ruam" className="w-full h-auto object-contain border border-white/10" />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SYNTHESIS */}
      <section data-testid="redbull-synthesis" className="mx-auto max-w-[1500px] px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.9] max-w-4xl">
            The sport changed. The operating instinct didn't.
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-8">
          {SYNTHESIS.map((s) => (
            <Reveal key={s.sport}>
              <p className="text-sm uppercase tracking-[0.25em] font-bold text-[#e62429]">{s.sport}</p>
              <p className="mt-3 font-display uppercase text-3xl sm:text-4xl text-white leading-none border-b border-white/15 pb-5">
                {s.word}
              </p>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <p className="mt-16 font-display text-4xl sm:text-6xl text-neutral-400">
            2016 <span className="text-[#e62429]">→</span> 2026
          </p>
          <p className="mt-6 max-w-2xl text-lg sm:text-xl leading-relaxed text-neutral-300">
            A decade of assignments across changing briefs, properties, sports and cities.
          </p>
          <p className="mt-8 text-sm uppercase tracking-[0.3em] font-bold text-[#e62429]">
            Oneworld of Sports × Red Bull
          </p>
        </Reveal>
      </section>

      <ChapterNav currentKey="redbull" />

      {/* Navigation out */}
      <section className="border-t border-white/10 py-16">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/work"
            data-testid="redbull-back-work-bottom"
            className="group inline-flex items-center gap-3 border border-white/20 px-8 py-4 text-xs uppercase tracking-[0.28em] font-bold text-white hover:bg-white hover:text-black transition-colors"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
            Back to Work
          </Link>
          <Link
            to="/#gateways"
            data-testid="redbull-back-explore-bottom"
            className="group inline-flex items-center gap-3 px-6 py-4 text-xs uppercase tracking-[0.28em] font-bold text-neutral-400 hover:text-white transition-colors"
          >
            Back to Explore
            <ArrowUpRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
          </Link>
        </div>
      </section>
    </div>
  );
}
